/* eslint-disable react-hooks/exhaustive-deps */
import { ModalUnderlay, Loader } from "uikit"
import { ReactComponent as OrganizeIcon } from "uikit/Icons/organize.svg"
import { ReactComponent as PlanIcon } from "uikit/Icons/plan.svg"
import { ReactComponent as UnderstandIcon } from "uikit/Icons/understand.svg"
import DownloadCase from "components/DownloadCase"
import Logout from "components/Logout"
import ScenarioView from "components/ScenarioView"
import TabNav from "components/TabNav"
import React, { lazy, Suspense, useState, useEffect, useRef } from "react"
import { Route, Switch, Redirect, withRouter } from "react-router-dom"
import { useStore } from "store"
import HouseholdProfile from "./HouseholdProfile"
import HouseholdsBrowser from "./HouseholdsBrowser"
import { ManageLinkedAccounts } from "./ManageLinkedAccounts"
import { AccountsAndProperty } from "./Planner/organize/AccountsAndProperty"
import { IncomeAndBenefits } from "./Planner/organize/IncomeAndBenefits"
import { Introduction as OrganizeIntroduction } from "./Planner/organize/Introduction"
import { GoalsAndDecisionsView } from "./Planner/plan/GoalsAndDecisions"
import { Introduction as PlanIntroduction } from "./Planner/plan/Introduction"
import { Investments } from "./Planner/plan/Investments"
import { Introduction as UnderstandIntroduction } from "./Planner/understand/Introduction"
import Loading from "components/Loading"
import { SurveyPollModal } from "components/accounts/SurveyPoll"
import BillingPortal from "./Billing/BillingPortal"

const UserProfile = lazy(() => import("./UserProfile"))
const Billing = lazy(() => import("./Billing"))
const ActionPlanView = lazy(() => import("./Planner/plan/ActionPlan"))
const InsightsView = lazy(() => import("./Planner/understand/Insights"))
const TheBigPicture = lazy(() => import("./Planner/understand/TheBigPicture"))

const Budgeting = lazy(() => import("components/budgeting/Budgeting"))
const CourseScreen = lazy(() => import("components/course/CourseScreen"))
const Learning = lazy(() => import("components/learning/Learning"))
const HomeScreen = lazy(() => import("components/homescreen/HomeScreen"))
const Agreements = lazy(() => import("./Signup/Agreements"))

// ask all users to reassess their confidence level after this date
// implemented for all users but motivated by bioMerieux wellness challenge
const REPOLL_DATE = new Date(2024, 4, 11) // 2024-05-11 (stupid JavaScript months)

const DirectProposalLink = props => {
  const [loadingSucceeded, setLoadingSucceeded] = useState(false)
  const [loadingFailed, setLoadingFailed] = useState(false)

  const [
    proposal,
    { setCurrentHouseholdId, updateHouseholdState, loadProposalStatus }
  ] = useStore("proposals", props.proposalId)

  useEffect(() => {
    const houseHoldActions = async (householdId, scenario_id) => {
      try {
        await setCurrentHouseholdId(householdId)
        await updateHouseholdState({
          id: householdId,
          currentScenarioId: scenario_id
        })
        await loadProposalStatus(proposal.id)
        setLoadingSucceeded(true)
      } catch (err) {
        setLoadingFailed(true)
      }
    }
    if (proposal) {
      const {
        scenario_id,
        household: { id: householdId }
      } = proposal
      houseHoldActions(householdId, scenario_id)
    }
  }, [
    proposal,
    setCurrentHouseholdId,
    updateHouseholdState,
    loadProposalStatus
  ])

  if (loadingSucceeded) {
    return <Redirect to="/plan/proposal" />
  }
  if (loadingFailed) {
    return <Redirect to="/" />
  }
  return <Loader />
}

const TabbedView = ({
  user,
  household,
  people,
  householdState,
  polls,
  showOrganizeIntroduction,
  showUnderstandIntroduction,
  showPlanIntroduction,
  pathname
}) => {
  const confidencePollTimeout = useRef(null)
  const [showConfidencePoll, setShowConfidencePoll] = useState(false)
  const hasOrganized =
    household.has_confirmed_accounts &&
    household.has_confirmed_properties &&
    household.has_confirmed_incomes &&
    household.has_confirmed_benefits
  const hasUnderstood = household.has_confirmed_spending
  const [accounts] = useStore("accounts")
  const householdAccounts = Object.values(accounts).filter(
    ({ household_id }) => household.id === household_id
  )
  const hasUnconfirmedAccounts =
    !household.has_confirmed_accounts ||
    householdAccounts.some(({ is_confirmed }) => !is_confirmed)
  const skipHowYouSpend = !householdAccounts.some(
    ({ type }) => type === "creditcard" || type === "checking"
  )

  const [subscription] = useStore("subscriptions", household.id)
  const isCOVID =
    subscription && subscription.features && subscription.features.is_covid

  const [scenariosObject] = useStore("householdScenarios", household.id)
  const scenarios = Object.values(scenariosObject)
  const [proposal] = useStore(
    "latestProposalState",
    householdState.currentScenarioId
  )
  const [proposals] = useStore(
    "scenarioProposals",
    householdState.currentScenarioId
  )

  useEffect(() => {
    if (
      user.group_type !== "advisor" &&
      !confidencePollTimeout.current &&
      (!polls.responded_at ||
        polls.responded_at === "" ||
        new Date(polls.responded_at) < REPOLL_DATE)
    ) {
      confidencePollTimeout.current = setTimeout(() => {
        clearTimeout(confidencePollTimeout.current)
        setShowConfidencePoll(true)
      }, 0)
    } else if (
      user.group_type !== "advisor" &&
      !confidencePollTimeout.current &&
      Object.keys(proposals).length === 1 &&
      new Date(proposal.completed_at) > new Date(polls.responded_at)
    ) {
      confidencePollTimeout.current = setTimeout(() => {
        clearTimeout(confidencePollTimeout.current)
        setShowConfidencePoll(true)
      }, 180000)
    }
    return () => {
      if (confidencePollTimeout.current) {
        clearTimeout(confidencePollTimeout.current)
      }
    }
  }, [])

  const actions = useStore()
  const hasProposal = Object.keys(proposal).length > 0
  if (
    (hasUnconfirmedAccounts || !hasOrganized) &&
    (pathname.includes("/plan") || pathname.includes("understand"))
  ) {
    return <Redirect to="/organize" />
  }

  return (
    <>
      {showConfidencePoll && (
        <SurveyPollModal
          onClose={() => setShowConfidencePoll(false)}
          household={household}
        />
      )}
      <TabNav pathname={pathname}>
        <TabNav.Section
          name="organize"
          title="ORGANIZE"
          skipIf={() => false}
          icon={OrganizeIcon}
          showIntroduction={() => showOrganizeIntroduction}
        >
          <TabNav.Introduction>
            <OrganizeIntroduction
              continueUrl={
                (polls.confidence == null || polls.confidence === "None") &&
                user.group_type !== "advisor" &&
                !isCOVID
                  ? "/organize/accounts+property/survey"
                  : "/organize/accounts+property/getting-started"
              }
            />
          </TabNav.Introduction>
          <TabNav.SubSection
            name="accounts+property"
            title="Accounts + Property"
            skipIf={() => false}
            component={() => (
              <Suspense fallback={<Loading />}>
                <AccountsAndProperty
                  household={household}
                  people={people}
                  user={user}
                />
              </Suspense>
            )}
          />
          <TabNav.SubSection
            name="income+benefits"
            title="Income + Benefits"
            disabled={hasUnconfirmedAccounts}
            skipIf={() => false}
            component={() => (
              <Suspense fallback={<Loading />}>
                <IncomeAndBenefits household={household} />
              </Suspense>
            )}
          />
        </TabNav.Section>
        <TabNav.Section
          name="understand"
          title="UNDERSTAND"
          disabled={hasUnconfirmedAccounts || !hasOrganized}
          cornerLabel={
            hasUnconfirmedAccounts || !hasOrganized
              ? { icon: "lock", color: "transparent" }
              : null
          }
          skipIf={() => false}
          icon={UnderstandIcon}
          showIntroduction={() => showUnderstandIntroduction}
        >
          <TabNav.Introduction>
            <UnderstandIntroduction
              household={household}
              continueUrl="/understand/spending"
            />
          </TabNav.Introduction>
          <TabNav.SubSection
            subNavigationVisibility="true"
            name="spending"
            title="The Big Picture"
            skipIf={() => false}
            component={() => (
              <Suspense fallback={<Loading />}>
                <TheBigPicture household={household} />
              </Suspense>
            )}
          />
          <TabNav.SubSection
            subNavigationVisibility="true"
            name="insights"
            title="Insights"
            skipIf={() => skipHowYouSpend}
            component={() => (
              <Suspense fallback={<Loading />}>
                <InsightsView household={household} />
              </Suspense>
            )}
          />
        </TabNav.Section>
        <TabNav.Section
          name="plan"
          title="PLAN"
          disabled={hasUnconfirmedAccounts || !hasUnderstood}
          cornerLabel={
            hasUnconfirmedAccounts || !hasUnderstood
              ? { icon: "lock", color: "transparent" }
              : null
          }
          skipIf={() => false}
          icon={PlanIcon}
          showIntroduction={() => showPlanIntroduction && !isCOVID}
        >
          <TabNav.Introduction>
            <Suspense fallback={<Loading />}>
              {scenarios.length > 1 && (
                <ScenarioView
                  user={user}
                  household={household}
                  render={() => (
                    <PlanIntroduction
                      continueUrl={"/plan/goals+decisions/getting-started"}
                    />
                  )}
                />
              )}
              <PlanIntroduction
                continueUrl={"/plan/goals+decisions/getting-started"}
              />
            </Suspense>
          </TabNav.Introduction>
          <TabNav.SubSection
            name="proposal"
            hideSubnavigation
            skip={() => householdState.isEditingPlan}
            skipIf={() => !hasProposal}
            title="Action Plan"
            component={() => (
              <Suspense fallback={<Loading />}>
                <ScenarioView
                  user={user}
                  household={household}
                  render={({ scenario }) => (
                    <ActionPlanView
                      user={user}
                      household={household}
                      scenario={scenario}
                      proposal={proposal}
                      actions={actions}
                      householdState={householdState}
                    />
                  )}
                />
              </Suspense>
            )}
          />

          <TabNav.SubSection
            name="goals+decisions"
            title="Goals + Decisions"
            component={() => (
              <Suspense fallback={<Loading />}>
                <ScenarioView
                  user={user}
                  household={household}
                  render={({ scenario }) => (
                    <GoalsAndDecisionsView
                      household={household}
                      scenario={scenario}
                      actions={actions}
                      householdState={householdState}
                    />
                  )}
                />
              </Suspense>
            )}
          />
          <TabNav.SubSection
            name="investments"
            title="Investments"
            component={() => (
              <ScenarioView
                user={user}
                household={household}
                render={() => (
                  <Investments household={household} actions={actions} />
                )}
              />
            )}
          />
        </TabNav.Section>
      </TabNav>
    </>
  )
}

const AppViews = props => {
  const {
    user,
    household,
    setCurrentHousehold,
    createHousehold,
    pathname
  } = props

  const actions = useStore()
  const [privileges] = useStore("privileges", props.user.group_id)
  const [scenarios, { getScenarioCase }] = useStore(
    "householdScenarios",
    household.id
  )
  const [newAgreements, { acceptAgreement }] = useStore("newAgreements")
  const [householdState] = useStore("householdState", household.id)
  const [polls] = useStore("polls", household.id)
  const [people] = useStore("householdPeople", household.id)
  const [householdAccountIds] = useStore("householdAccountIds", household.id)
  const [scenarioGoalIds] = useStore(
    "scenarioGoalIds",
    householdState.currentScenarioId
  )

  if (pathname === "/") {
    const lastLocation = window.localStorage.getItem("lastLocation")
    if (lastLocation) {
      return <Redirect to={lastLocation} />
    }
    return <Redirect to="/home" />
  }

  if (pathname !== "/signout") {
    window.localStorage.setItem("lastLocation", pathname)
  }
  const hasVitality = !!(user.metadata && user.metadata.vitality_id)

  return (
    <Switch>
      <Route
        path="/household-profile"
        render={() => (
          <ModalUnderlay>
            <HouseholdProfile household={household} />
          </ModalUnderlay>
        )}
      />
      {user.group_type === "advisor" ? (
        <Route
          path="/browse-households"
          render={() => (
            <ModalUnderlay>
              <HouseholdsBrowser
                {...{
                  household,
                  setCurrentHousehold: setCurrentHousehold,
                  currentHouseholdId: household.id,
                  createHousehold,
                  canCreateHousehold: privileges["household:create"] || false,
                  user: props.user
                }}
              />
              )
            </ModalUnderlay>
          )}
        />
      ) : null}
      <Route
        path="/user-profile"
        render={() => (
          <ModalUnderlay>
            <UserProfile />
          </ModalUnderlay>
        )}
      />
      <Route
        path="/billing"
        render={() => (
          <ModalUnderlay>
            <Billing />
          </ModalUnderlay>
        )}
      />
      <Route
        path="/billing-portal"
        render={({ location }) => (
          <BillingPortal location={location.state.pathname} />
        )}
      />

      <Route path="/signout" render={() => <Logout logout={props.logout} />} />
      <Route
        path="/download-case"
        render={() => (
          <DownloadCase
            getScenarioCase={getScenarioCase}
            scenarioId={Object.values(scenarios)[0].id}
          />
        )}
      />
      <Route
        path="/manage-linked-accounts"
        render={() => (
          <ModalUnderlay>
            <ManageLinkedAccounts household={household} actions={actions} />
          </ModalUnderlay>
        )}
      />
      <Route
        path="/review/:proposalId"
        exact
        render={({
          match: {
            params: { proposalId }
          }
        }) => <DirectProposalLink proposalId={proposalId} />}
      />
      <Route
        path="/agreements"
        render={() => (
          <Suspense fallback={<Loading />}>
            <Agreements
              agreements={newAgreements}
              onSuccess={"/organize/accounts+property"}
              onAccept={acceptAgreement}
              isSignin={true}
              url={"/agreements"}
            />
          </Suspense>
        )}
      />
      <Route
        path="/budgeting"
        render={() => (
          <Suspense fallback={<Loading />}>
            <Budgeting />
          </Suspense>
        )}
      />
      <Route
        path="/home"
        render={() => (
          <Suspense fallback={<Loading />}>
            <HomeScreen hasVitality={hasVitality} />
          </Suspense>
        )}
      />
      <Route
        path="/learning"
        render={() => (
          <Suspense fallback={<Loading />}>
            <Learning hasVitality={hasVitality} />
          </Suspense>
        )}
      />
      <Route
        path="/courses/:courseId"
        exact
        render={({
          match: {
            params: { courseId }
          }
        }) => (
          <Suspense fallback={<Loading />}>
            <CourseScreen courseId={courseId} />
          </Suspense>
        )}
      />
      <Route
        path="/"
        render={() => (
          <TabbedView
            user={props.user}
            pathname={pathname}
            household={household}
            people={people}
            householdState={householdState}
            polls={polls}
            showOrganizeIntroduction={householdAccountIds.length === 0}
            showUnderstandIntroduction={!props.household.has_confirmed_spending}
            showPlanIntroduction={scenarioGoalIds.length === 0}
          />
        )}
      />
      <Redirect to="/home" />
    </Switch>
  )
}

export default withRouter(AppViews)
